import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Button from './Button';
import './Modal.css';

/**
 * 模态框组件
 * Modal Component
 * 
 * @param {Object} props
 * @param {boolean} props.open - 是否打开
 * @param {Function} props.onClose - 关闭回调
 * @param {string} props.title - 标题
 * @param {React.ReactNode} props.children - 内容
 * @param {React.ReactNode} props.footer - 底部内容
 * @param {'sm'|'md'|'lg'|'xl'} props.size - 模态框尺寸
 * @param {boolean} props.closable - 是否可关闭
 * @param {boolean} props.maskClosable - 点击遮罩是否关闭
 * @param {string} props.className - 额外的CSS类名
 */
const Modal = ({
  open = false,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  closable = true,
  maskClosable = true,
  className = '',
  ...props
}) => {
  const modalRef = useRef(null);
  const previousActiveElement = useRef(null);

  // 处理键盘事件
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && closable && onClose) {
        onClose();
      }
    };

    if (open) {
      // 保存当前焦点元素
      previousActiveElement.current = document.activeElement;
      
      // 添加键盘监听
      document.addEventListener('keydown', handleKeyDown);
      
      // 防止背景滚动
      document.body.style.overflow = 'hidden';
      
      // 设置焦点到模态框
      setTimeout(() => {
        if (modalRef.current) {
          modalRef.current.focus();
        }
      }, 100);
    } else {
      // 恢复焦点
      if (previousActiveElement.current) {
        previousActiveElement.current.focus();
      }
      
      // 恢复背景滚动
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, closable, onClose]);

  // 处理遮罩点击
  const handleMaskClick = (e) => {
    if (e.target === e.currentTarget && maskClosable && onClose) {
      onClose();
    }
  };

  // 处理关闭按钮点击
  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  if (!open) {
    return null;
  }

  const baseClass = 'modal';
  const sizeClass = `modal--${size}`;
  
  const modalClass = [
    baseClass,
    sizeClass,
    className
  ].filter(Boolean).join(' ');

  const modalContent = (
    <div className="modal-overlay" onClick={handleMaskClick}>
      <div
        ref={modalRef}
        className={modalClass}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'modal-title' : undefined}
        {...props}
      >
        {/* 模态框头部 */}
        {(title || closable) && (
          <div className="modal-header">
            {title && (
              <h2 id="modal-title" className="modal-title">
                {title}
              </h2>
            )}
            
            {closable && (
              <Button
                variant="secondary"
                size="sm"
                className="modal-close"
                onClick={handleClose}
                aria-label="关闭模态框"
                icon={
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                }
              />
            )}
          </div>
        )}
        
        {/* 模态框内容 */}
        <div className="modal-body">
          {children}
        </div>
        
        {/* 模态框底部 */}
        {footer && (
          <div className="modal-footer">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  // 使用 Portal 渲染到 body
  return createPortal(modalContent, document.body);
};

export default Modal;
